<template>
  <div class="loan-foreclosure-view relative overflow-hidden">
    <!-- Watermark for closed loans -->
    <div v-if="!isValidForPayment" class="loan-status-watermark" :class="closedStatusColorClass">
      {{ closedStatusText }}
    </div>
    <!-- Borrower Selection Tabs at Top -->
    <v-card v-if="borrowerTabs.length > 1" flat solo>
      <v-tabs v-model="activeBorrowerTab" class="mb-2" color="primary">
        <v-tab v-for="borrower in borrowerTabs" :key="borrower.id" class="font-medium">
          <div class="text-center text-sm">
            {{ borrower.name }}
            <div class="text-xs text-gray-600">({{ borrower.customerData.customerFullName }})</div>
          </div>
        </v-tab>
      </v-tabs>
    </v-card>

    <div class="flex gap-4">
      <!-- Foreclosure Summary -->
      <v-card class="mb-4 w-1/2" outlined>
        <v-card-title class="text-base pb-2">
          <v-icon left color="success">mdi-cash-multiple</v-icon>
          Account Summary
        </v-card-title>
        <v-card-text>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div class="summary-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">Loan Amount</p>
              <p class="text-md font-semibold text-blue-600 mb-2">
                ₹{{ formatCurrency(getBorrowerLoanAmount()) }}
              </p>
            </div>
            <div class="summary-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">Principal Paid</p>
              <p class="text-md font-semibold text-green-600 mb-2">
                ₹{{ formatCurrency(calculatePrincipalPaid()) }}
              </p>
            </div>
            <div class="summary-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">Principal Balance</p>
              <p class="text-md font-semibold text-orange-600 mb-2">
                ₹{{ formatCurrency(foreclosureData.principalOutstanding) }}
              </p>
            </div>
            <div class="summary-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">Interest Paid</p>
              <p class="text-md font-semibold text-purple-600 mb-2">
                ₹{{ formatCurrency(calculateInterestPaid()) }}
              </p>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- EMI Status Overview -->
      <v-card class="mb-4 w-1/2" outlined>
        <v-card-title class="pb-2 text-base">
          <v-icon left color="info">mdi-chart-pie</v-icon>
          EMI(s) Overview
        </v-card-title>
        <v-card-text>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div class="status-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">Total</p>
              <p class="text-md font-semibold mb-2">{{ emiStatusData.totalEmis }}</p>
            </div>
            <div class="status-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">Paid</p>
              <p class="text-md font-semibold text-green-600 mb-2">{{ emiStatusData.paidEmis }}</p>
            </div>
            <div class="status-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">Pending</p>
              <p class="text-md font-semibold text-red-600 mb-2">{{ emiStatusData.pendingEmis }}</p>
            </div>
            <div class="status-item dark-bg-custom">
              <p class="text-sm text-gray-600 mb-2">Overdue</p>
              <p class="text-md font-semibold text-orange-600 mb-2">
                {{ emiStatusData.overdueEmis }}
              </p>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>
    <!-- Detailed Breakdown -->

    <!-- <v-card class="mb-6" outlined>
      <v-card-title class="pb-2">
        <v-icon left color="warning">mdi-file-document-edit</v-icon>
        EMI Details
      </v-card-title>
      <v-card-text>
        <v-expansion-panels v-model="expandedPanel" multiple class="border" flat>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div class="flex items-center">
                <v-icon class="mr-2" color="purple">mdi-format-list-numbered</v-icon>
                EMI(s)
                <span v-if="currentBorrower" class="ml-2 text-sm text-gray-600">
                  ({{ currentBorrowerEmiSchedule.length }})
                </span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead>
                    <tr class="border-b">
                      <th class="text-left p-2">EMI #</th>
                      <th class="text-left p-2">Due Date</th>
                      <th class="text-left p-2">EMI Amount</th>
                      <th class="text-left p-2">Principal</th>
                      <th class="text-left p-2">Interest</th>
                      <th class="text-left p-2">Status</th>
                      <th class="text-left p-2">Outstanding</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(emi, index) in currentBorrowerEmiSchedule"
                      :key="emi._id"
                      class="border-b hover:bg-gray-50"
                    >
                      <td class="p-2 font-medium">{{ index + 1 }}</td>
                      <td class="p-2">{{ formatDate(emi.dueDate) }}</td>
                      <td class="p-2">₹{{ formatCurrency(emi.emiAmount) }}</td>
                      <td class="p-2">₹{{ formatCurrency(emi.principalAmount) }}</td>
                      <td class="p-2">₹{{ formatCurrency(emi.interestAmount) }}</td>
                      <td class="p-2">
                        <v-chip
                          x-small
                          :color="getStatusColor(emi.emiStatus).bgColor"
                          :text-color="getStatusColor(emi.emiStatus).textColor"
                        >
                          {{ emi.emiStatus }}
                        </v-chip>
                      </td>
                      <td class="p-2">₹{{ formatCurrency(emi.outstandingAmount) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card> -->
    <!-- Prepayment History -->
    <v-card
      v-if="loanDetails.prePayments && loanDetails.prePayments.length > 0"
      class="mb-6"
      outlined
    >
      <v-card-title class="pb-2 text-base">
        <v-icon left color="primary">mdi-history</v-icon>
        Prepayment History
      </v-card-title>
      <v-card-text>
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left border rounded-lg">
            <thead class="bg-gray-50 border-b">
              <tr>
                <th class="p-3 font-semibold text-gray-600">Date</th>
                <th class="p-3 font-semibold text-gray-600">Total Amount Paid</th>
                <th class="p-3 font-semibold text-gray-600">Principal Adjusted</th>
                <th class="p-3 font-semibold text-gray-600">Interest Adjusted</th>
                <th class="p-3 font-semibold text-gray-600">Status</th>
                <th class="p-3 font-semibold text-gray-600 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="prePayment in loanDetails.prePayments"
                :key="prePayment._id"
                class="border-b transition-all"
                :class="prePayment.isDeleted ? 'bg-red-50/50 opacity-60' : 'hover:bg-gray-50'"
              >
                <td class="px-3 py-2 whitespace-nowrap">
                   <span :class="{ 'line-through text-red-600': prePayment.isDeleted }">{{ formatDate(prePayment.date) }}</span>
                   <v-chip v-if="prePayment.isDeleted" x-small color="red" dark label class="ml-1 font-bold">Deleted</v-chip>
                </td>
                <td class="px-3 py-2 font-medium" :class="{ 'line-through opacity-50': prePayment.isDeleted }">
                  ₹{{ formatCurrency(prePayment.totalAmtPaid) }}
                </td>
                <td class="px-3 py-2" :class="{ 'line-through opacity-50': prePayment.isDeleted }">₹{{ formatCurrency(prePayment.actualPrincipalAmt) }}</td>
                <td class="px-3 py-2" :class="{ 'line-through opacity-50': prePayment.isDeleted }">₹{{ formatCurrency(prePayment.accruedIntrest) }}</td>
                <td class="px-3 py-2">
                  <v-chip
                    x-small
                    :color="prePayment.isDeleted ? 'red darken-2' : (prePayment.status === 'Partial Settlement' ? 'blue' : 'green')"
                    text-color="white"
                    class="font-bold"
                  >
                    {{ prePayment.isDeleted ? 'Deleted' : (prePayment.status || 'Settled') }}
                  </v-chip>
                </td>
                <td class="px-3 py-2 text-center">
                  <v-btn
                    v-if="!prePayment.isDeleted"
                    x-small
                    color="primary"
                    outlined
                    class="text-none font-bold"
                    @click="downloadPrePaymentSummary(prePayment)"
                    :loading="pdfGenerating && selectedPrePaymentPdfData === prePayment"
                  >
                    <v-icon left x-small>mdi-download</v-icon> Download
                  </v-btn>
                  <v-icon v-else color="grey lighten-1">mdi-cancel</v-icon>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card-text>
    </v-card>

    <!-- Payment Configuration -->
    <v-card v-if="isValidForPayment" class="" outlined>
      <v-card-title class="pb-2 text-base">
        <v-icon left color="primary">mdi-calculator</v-icon>
        Payment
      </v-card-title>
      <v-card-text>
        <div v-if="loanClosureType === 'prepayment'" class="mt-4">
          <!-- Full Payment Checkbox -->
          <div class="mb-4 p-3 bg-blue-50 dark-bg-custom rounded border border-blue-200">
            <v-checkbox v-model="isFullPayment" @change="handleFullPaymentToggle" class="mb-2">
              <template v-slot:label>
                <div>
                  <span class="font-medium">Pay full amount</span>
                  <div class="text-sm text-gray-600">
                    Total Due: ₹{{ formatCurrency(getTotalAmountDue()) }}
                    <span class="text-xs">(Principal + Accrued Interest)</span>
                  </div>
                </div>
              </template>
            </v-checkbox>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <v-text-field
              v-model.number="partialAmount"
              label="Payment Amount"
              prefix="₹"
              type="number"
              outlined
              dense
              :min="0"
              :disabled="isFullPayment"
              :rules="paymentAmountRules"
              :error-messages="getPaymentAmountErrors()"
              @input="validateAndCalculate"
              :hint="`Maximum: ₹${formatCurrency(maxPaymentAmount)}`"
              persistent-hint
            ></v-text-field>
            <v-text-field
              v-model.number="foreclosureData.accruedInterest"
              label="Accrued Interest"
              prefix="₹"
              type="number"
              outlined
              dense
              :min="0"
              :rules="accruedInterestRules"
              :error-messages="getAccruedInterestErrors()"
              @input="onAccruedInterestChange"
              persistent-hint
            ></v-text-field>
          </div>

          <!-- Prepayment Application Options -->
          <div class="mt-4">
            <h4 class="font-semibold mb-3">How should the prepayment be applied?</h4>
            <v-radio-group
              v-model="prepaymentApplication"
              @change="calculateForeclosure"
              :disabled="isFullPayment"
            >
              <v-radio
                label="Reduce Loan Tenure (Keep emi amount same)"
                value="reduce_tenure"
              ></v-radio>
              <v-radio label="Reduce Emi Amount (Keep tenure same)" value="reduce_emi"></v-radio>
              <v-radio label="Hybrid (Balanced emi and tenure)" value="custom_tenure"></v-radio>
            </v-radio-group>

            <!-- Custom Tenure Slider -->
            <div
              v-if="prepaymentApplication === 'custom_tenure'"
              class="mt-4 p-4 bg-blue-50 rounded border"
            >
              <h5 class="font-medium mb-3">Hybrid Payment Configuration</h5>
              <p class="text-sm text-gray-600 mb-3">
                Make a prepayment of ₹{{ formatCurrency(partialAmount || 0) }} and select your loan
                closure EMI position. The slider shows EMI positions from 1 to
                {{ emiStatusData.totalEmis }}. Moving left reduces loan tenure and increases monthly
                EMI amount. Paid EMIs (1-{{ emiStatusData.paidEmis }}) and cancelled EMIs are
                disabled.
              </p>
              <div class="mb-4">
                <CustomSlider
                  v-model="customTenure"
                  :min="1"
                  :max="emiStatusData.totalEmis"
                  :step="1"
                  :breakpoints="sliderBreakpoints"
                  :disabledRanges="customSliderDisabledRanges"
                  :activeColor="'#3b82f6'"
                  :disabledColor="'#e5e7eb'"
                  :breakpointColor="'#f59e0b'"
                  @input="onTenureSliderChange"
                  :disabled="isFullPayment"
                />
                <div class="flex justify-between text-sm text-gray-600 mt-2">
                  <span>First EMI: 1</span>
                  <span>Closure at EMI: {{ customTenure }}</span>
                  <span>Last EMI: {{ maxTenure }}</span>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="bg-white p-3 rounded border">
                  <p class="text-sm text-gray-600">New EMI Amount</p>
                  <p class="text-lg font-semibold text-blue-600">
                    ₹{{ formatCurrency(customEmiAmount) }}
                  </p>
                  <p class="text-xs text-gray-500">
                    {{ customEmiAmount > currentEmiAmount ? 'Increased' : 'Decreased' }} by ₹{{
                      formatCurrency(Math.abs(customEmiAmount - currentEmiAmount))
                    }}
                  </p>
                </div>
                <div class="bg-white p-3 rounded border">
                  <p class="text-sm text-gray-600">Tenure Change</p>
                  <p class="text-lg font-semibold" :class="tenureChangeColor">
                    {{ tenureChangeText }}
                  </p>
                  <p class="text-xs text-gray-500">
                    Loan closes at EMI: {{ customTenure }} of {{ emiStatusData.totalEmis }}
                    <br />
                    EMIs Saved: {{ Math.max(0, rightmostEnabledPosition - customTenure) }}
                  </p>
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <ChargesDiscountsSection
                  type="penalties"
                  :items="penaltyCharges"
                  :format-currency="formatCurrency"
                  :amount-rules="amountRules"
                  @charges-changed="onPenaltyChargesChanged"
                />
              </div>
              <div>
                <ChargesDiscountsSection
                  type="discounts"
                  :items="discountWaivers"
                  :format-currency="formatCurrency"
                  :amount-rules="amountRules"
                  :max-discount="getTotalAmountBeforeDiscounts()"
                  @discounts-changed="onDiscountWaiversChanged"
                />
              </div>
            </div>

            <!-- Payment Summary & Impact -->
            <div class="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 dark-bg-custom">
              <!-- Payment Details -->
              <div>
                <h4 class="font-semibold mb-3">Payment Details</h4>
                <!-- <v-select
                  v-model="closureDetails.closureReason"
                  :items="closureReasons"
                  label="Payment Reason"
                  outlined
                  dense
                  class="mb-3"
                ></v-select> -->
                <v-textarea
                  v-model="closureDetails.remarks"
                  label="Payment Remarks"
                  outlined
                  dense
                  rows="2"
                ></v-textarea>
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="closureDetails.closureDate"
                      label="Payment Date"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="closureDetails.closureDate"
                    @input="dateMenu = false"
                    :max="todayDate"
                  ></v-date-picker>
                </v-menu>
                <div class="space-y-3 p-4 payment-summary rounded my-6" v-if="prepaymentImpact">
                  <div class="space-y-2 text-sm" v-if="prepaymentImpact">
                    <h5 class="font-semibold mb-2 text-blue-800 pt-1">Prepayment Impact:</h5>
                    <div
                      v-if="prepaymentApplication === 'reduce_tenure'"
                      class="flex justify-between"
                    >
                      <span>Tenure Reduction:</span>
                      <span class="font-medium text-blue-600">
                        {{ prepaymentImpact.tenureReduction }} months
                      </span>
                    </div>
                    <div v-if="prepaymentApplication === 'reduce_emi'" class="flex justify-between">
                      <span>New EMI Amount:</span>
                      <span class="font-medium text-blue-600">
                        ₹{{ formatCurrency(prepaymentImpact.newEmiAmount) }}
                      </span>
                    </div>
                    <div
                      v-if="prepaymentApplication === 'custom_tenure'"
                      class="flex justify-between"
                    >
                      <span>Custom Tenure:</span>
                      <span class="font-medium text-blue-600">
                        {{ prepaymentImpact.customTenure }} months
                      </span>
                    </div>
                    <div
                      v-if="prepaymentApplication === 'custom_tenure'"
                      class="flex justify-between"
                    >
                      <span>New EMI Amount:</span>
                      <span class="font-medium text-blue-600">
                        ₹{{ formatCurrency(prepaymentImpact.newEmiAmount) }}
                      </span>
                    </div>
                    <div class="flex justify-between">
                      <span>Interest Savings:</span>
                      <span class="font-medium text-green-600">
                        ₹{{ formatCurrency(prepaymentImpact.interestSavings) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Combined Payment Summary & Impact -->
              <div class="dark-bg-custom">
                <h4 class="font-semibold mb-3">Payment Summary</h4>
                <div class="space-y-3 p-4 payment-summary rounded mb-4">
                  <div class="flex justify-between">
                    <span>Principal Outstanding:</span>
                    <span class="font-medium">
                      ₹{{ formatCurrency(foreclosureData.principalOutstanding) }}</span
                    >
                  </div>

                  <div class="flex justify-between">
                    <span>Accrued Interest:</span>
                    <span class="font-medium"
                      >+ ₹{{ formatCurrency(foreclosureData.accruedInterest) }}</span
                    >
                  </div>
                  <div class="flex justify-between border-t pt-2">
                    <span></span>
                    <span class="font-semibold">₹{{ formatCurrency(getTotalAmountDue()) }}</span>
                  </div>
                  <div class="flex justify-between" v-if="!isFullPayment">
                    <span>Prepayment Principal:</span>
                    <span class="font-medium text-green-600">
                      - ₹{{ formatCurrency(prepaymentImpact?.prepaymentAmount || 0) }}
                    </span>
                  </div>
                  <div class="flex justify-between border-t pt-2" v-if="!isFullPayment">
                    <span></span>
                    <span class="font-semibold"
                      >₹{{
                        formatCurrency(
                          getTotalAmountDue() - (prepaymentImpact?.prepaymentAmount || 0)
                        )
                      }}</span
                    >
                  </div>
                  <div class="flex justify-between">
                    <span>Total Penalties:</span>
                    <span class="font-medium text-red-600"
                      >+ ₹{{ formatCurrency(getTotalPenaltyCharges()) }}</span
                    >
                  </div>
                  <div class="flex justify-between border-t pt-2">
                    <span></span>
                    <span class="font-semibold"
                      >₹{{
                        formatCurrency(
                          getTotalAmountDue() -
                            prepaymentImpact?.prepaymentAmount +
                            getTotalPenaltyCharges()
                        )
                      }}</span
                    >
                  </div>
                  <div class="flex justify-between">
                    <span>Total Discounts:</span>
                    <span class="font-medium text-green-600"
                      >- ₹{{ formatCurrency(foreclosureData.totalDiscounts) }}</span
                    >
                  </div>
                  <div class="flex justify-between border-t pt-2" v-if="isFullPayment">
                    <span class="text-lg font-bold">Full Amount Payable:</span>
                    <span class="font-medium">
                      ₹{{ formatCurrency(foreclosureData.totalForeclosureAmount) }}
                    </span>
                  </div>
                  <div class="flex justify-between border-t pt-2" v-else>
                    <span class="text-lg font-bold">Net Outstanding Principal:</span>
                    <span class="font-medium">
                      ₹{{
                        formatCurrency(
                          getTotalAmountDue() -
                            (prepaymentImpact?.prepaymentAmount || 0) +
                            getTotalPenaltyCharges() -
                            foreclosureData.totalDiscounts
                        )
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Action Buttons -->

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="headline"
          >Confirm
          {{ loanClosureType === 'foreclosure' ? 'Foreclosure' : 'Partial Payment' }}
        </v-card-title>
        <v-card-text>
          <p v-if="!processing">
            Are you sure you want to process this
            {{ loanClosureType === 'foreclosure' ? 'foreclosure' : 'partial payment' }}
            <span v-if="currentBorrower"
              >for <strong>{{ currentBorrower.name }}</strong></span
            >?
          </p>

          <!-- Processing Status -->
          <div v-if="processing" class="text-center py-4">
            <v-progress-circular
              indeterminate
              color="primary"
              size="40"
              class="mb-3"
            ></v-progress-circular>
            <p class="mb-2">
              Processing
              {{ loanClosureType === 'foreclosure' ? 'foreclosure' : 'partial payment' }}...
            </p>
            <small class="text-gray-600">Please wait, this may take a moment.</small>
          </div>

          <div v-if="!processing" class="mt-4 p-4 bg-gray-100 rounded">
            <div class="flex justify-between mb-2">
              <span class="font-semibold">Final Amount:</span>
              <span class="font-semibold text-green-600"
                >₹{{ formatCurrency(foreclosureData.totalForeclosureAmount) }}</span
              >
            </div>
            <div
              v-if="loanClosureType === 'foreclosure'"
              class="flex justify-between text-sm text-gray-600"
            >
              <span>EMIs to be marked as paid:</span>
              <span>{{ unpaidEmiCount }} EMIs</span>
            </div>
            <div
              v-else-if="prepaymentImpact && prepaymentImpact.tenureReduction > 0"
              class="flex justify-between text-sm text-gray-600"
            >
              <span>Tenure reduction:</span>
              <span>{{ prepaymentImpact.tenureReduction }} months</span>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false" :disabled="processing">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="confirmForeclosure"
            :loading="processing"
            :disabled="processing"
          >
            {{ processing ? 'Processing...' : 'Confirm' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Workflow Section -->
    <v-card v-if="isValidForPayment" class="mb-6 elevation-0 border" outlined>
      <v-card-title class="text-base pb-2 font-bold text-blue-800">
        <v-icon left color="primary">mdi-format-list-checks</v-icon>
        Loan Closure Workflow Requirements
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-4">
        <WorkflowStepRenderer
          ref="workflowRef"
          entityType="LOAN_CLOSURE"
          :caseId="caseId"
          @workflow-saved="handleWorkflowSaved"
        />
      </v-card-text>
    </v-card>

    <div
      v-if="isValidForPayment"
      class="sticky bottom-0 bg-white dark-bg-custom z-10 border-t w-full flex justify-end space-x-4 p-2"
    >
      <v-btn outlined @click="resetCalculation">
        <v-icon left>mdi-refresh</v-icon>
        Reset
      </v-btn>
      <v-btn color="primary" @click="processForeclosure" :loading="processing">
        <v-icon left>mdi-check</v-icon>
        Continue
      </v-btn>
    </div>

    <ForeclosurePdfTemplate
      v-if="selectedPrePaymentPdfData"
      ref="prePaymentPdfComponent"
      :org-data="orgData"
      :closure-date="selectedPrePaymentPdfData.date"
      :customer-name="
        currentBorrower?.customerData?.customerFullName || currentBorrower?.name || 'Customer Name'
      "
      :father-name="currentBorrower?.customerData?.fatherName || 'Not available'"
      :customer-address="getCustomerAddress()"
      :loan-details="loanDetails"
      :foreclosure-data="{
        principalOutstanding: selectedPrePaymentPdfData.actualPrincipalAmt || 0,
        accruedInterest: selectedPrePaymentPdfData.accruedIntrest || 0
      }"
      :penalty-charges="getPrePaymentPenalty(selectedPrePaymentPdfData)"
      :discount-waivers="getPrePaymentDiscount(selectedPrePaymentPdfData)"
      :final-amount="selectedPrePaymentPdfData.totalAmtPaid || 0"
      :payment-summary="{
        closureType: 'prepayment',
        isFullPayment: false,
        prepaymentApplication: selectedPrePaymentPdfData.prepaymentApplication || 'reduce_tenure',
        amountPaid: selectedPrePaymentPdfData.totalAmtPaid || 0,
        originalEmi: selectedPrePaymentPdfData.originalEmiAmount || loanDetails.emiAmount || 0,
        newEmi: selectedPrePaymentPdfData.newEmiAmount || 0,
        originalTenure: emiStatusData.totalEmis,
        tenureReduction:
          (selectedPrePaymentPdfData.emis && selectedPrePaymentPdfData.emis.length) || 0,
        newTenure:
          emiStatusData.totalEmis -
          ((selectedPrePaymentPdfData.emis && selectedPrePaymentPdfData.emis.length) || 0)
      }"
    />

    <ForeclosurePdfTemplate
      ref="foreclosurePdfComponent"
      :org-data="orgData"
      :closure-date="closureDetails.closureDate"
      :customer-name="
        currentBorrower?.customerData?.customerFullName || currentBorrower?.name || 'Customer Name'
      "
      :father-name="currentBorrower?.customerData?.fatherName || 'Not available'"
      :customer-address="getCustomerAddress()"
      :loan-details="loanDetails"
      :foreclosure-data="foreclosureData"
      :penalty-charges="penaltyCharges"
      :discount-waivers="discountWaivers"
      :final-amount="getFinalAmount()"
      :payment-summary="{
        closureType: loanClosureType,
        isFullPayment: isFullPayment,
        prepaymentApplication: prepaymentApplication,
        amountPaid: getFinalAmount(),
        originalEmi: currentEmiAmount,
        newEmi: prepaymentImpact
          ? prepaymentImpact.newEmiAmount || currentEmiAmount
          : currentEmiAmount,
        originalTenure: originalRemainingTenure,
        newTenure: prepaymentImpact
          ? originalRemainingTenure - (prepaymentImpact.tenureReduction || 0)
          : originalRemainingTenure,
        tenureReduction: prepaymentImpact ? prepaymentImpact.tenureReduction || 0 : 0
      }"
    />
  </div>
</template>
<script>
import {
  getEmiStatusColor,
  calculateForeclosureAmount,
  validateForeclosureData,
  calculateTenure,
  calculateEMI
} from '@/utils/common'
import ChargesDiscountsSection from './ChargesDiscountsSection.vue'
import CustomSlider from './CustomSlider.vue'
import WorkflowStepRenderer from './WorkflowStepRenderer.vue'
import ForeclosurePdfTemplate from './ForeclosurePdfTemplate.vue'
import html2pdf from 'html2pdf.js'

export default {
  name: 'LoanForeclosureView',
  components: {
    ChargesDiscountsSection,
    CustomSlider,
    WorkflowStepRenderer,
    ForeclosurePdfTemplate
  },
  props: {
    loanDetails: {
      type: Object,
      required: true
    },
    emiSchedule: {
      type: Array,
      required: true
    },
    caseId: String,
    borrowerTabs: {
      type: Array,
      default: () => []
    },
    selectedCase: {
      type: Object,
      default: () => ({})
    }
  },
  watch: {
    caseId: {
      handler(newId) {
        if (newId) {
          this.calculateForeclosure()
        }
      },
      immediate: true
    },
    activeBorrowerTab: {
      handler() {
        this.manualAccruedInterest = false // Reset manual flag when switching borrowers
        this.initializeTenureSlider()
        this.calculateForeclosure()
      }
    },
    partialAmount: {
      handler() {
        if (this.prepaymentApplication === 'custom_tenure') {
          this.onTenureSliderChange()
        }
      }
    }
  },
  data() {
    return {
      loanClosureType: 'prepayment',
      partialAmount: 0,
      isFullPayment: false,
      prepaymentApplication: 'reduce_tenure', // Default option
      prepaymentImpact: null,
      expandedPanel: [],
      manualAccruedInterest: false, // Track if user manually changed accrued interest

      // Custom tenure slider properties
      customTenure: 0,
      customEmiAmount: 0,
      currentEmiAmount: 0,
      originalRemainingTenure: 0,
      minTenure: 1,
      maxTenure: 1, // Will be set to pending EMIs count
      sliderTotalRange: 60, // Total range for visual display
      dateMenu: false,
      confirmDialog: false,
      processing: false,
      pdfGenerating: false,
      selectedPrePaymentPdfData: null,
      activeBorrowerTab: 0, // For borrower tabs

      penaltyCharges: [{ chargeType: 'Prepayment Charges', amount: 0 }],

      discountWaivers: [{ discountType: 'Interest Waiver', amount: 0 }],

      closureDetails: {
        closureReason: '',
        remarks: '',
        closureDate: new Date().toISOString().substr(0, 10)
      },

      closureReasons: [
        'Full Payment',
        'Partial Settlement',
        'Legal Settlement',
        'Mutual Agreement',
        'Other'
      ],

      foreclosureData: {
        principalOutstanding: 0,
        principalPaid: 0,
        accruedInterest: 0,
        interestPaid: 0,
        penaltyAmount: 0,
        totalForeclosureAmount: 0,
        subtotal: 0,
        totalDiscounts: 0
      },

      closureWorkflow: null
    }
  },

  computed: {
    orgData() {
      return this.$storage.getUniversal('orgData') || {}
    },

    customSliderDisabledRanges() {
      const totalEmis = this.emiStatusData.totalEmis
      const paidEmis = this.emiStatusData.paidEmis

      // Find cancelled EMIs and their positions
      const cancelledEmiPositions = []
      this.currentBorrowerEmiSchedule.forEach((emi, index) => {
        if (emi.emiStatus === 'Cancelled') {
          cancelledEmiPositions.push(index + 1) // EMI positions are 1-based
        }
      })

      const ranges = []

      // Disable paid EMIs at the beginning (1 to paidEmis)
      if (paidEmis > 0) {
        ranges.push({ from: 1, to: paidEmis })
      }

      // Disable cancelled EMIs (usually at the end, but could be anywhere)
      if (cancelledEmiPositions.length > 0) {
        // Group consecutive cancelled EMI positions into ranges
        let rangeStart = cancelledEmiPositions[0]
        let rangeEnd = cancelledEmiPositions[0]

        for (let i = 1; i < cancelledEmiPositions.length; i++) {
          if (cancelledEmiPositions[i] === rangeEnd + 1) {
            // Consecutive cancelled EMI
            rangeEnd = cancelledEmiPositions[i]
          } else {
            // Non-consecutive, create a range for previous group
            ranges.push({ from: rangeStart, to: rangeEnd })
            rangeStart = cancelledEmiPositions[i]
            rangeEnd = cancelledEmiPositions[i]
          }
        }
        // Add the last range
        ranges.push({ from: rangeStart, to: rangeEnd })
      }

      console.log('Disabled ranges:', ranges)
      return ranges
    },

    // Find the rightmost enabled EMI position for slider initialization
    rightmostEnabledPosition() {
      const totalEmis = this.emiStatusData.totalEmis
      const disabledRanges = this.customSliderDisabledRanges

      // Start from the rightmost position and work backwards
      for (let position = totalEmis; position >= 1; position--) {
        const isDisabled = disabledRanges.some(
          (range) => position >= range.from && position <= range.to
        )
        if (!isDisabled) {
          return position
        }
      }

      // Fallback to 1 if no enabled position found (shouldn't happen in normal cases)
      return 1
    },

    // Calculate breakpoints for visual indicators on the slider
    sliderBreakpoints() {
      const breakpoints = []
      const paidEmis = this.emiStatusData.paidEmis

      // Add breakpoint at last paid EMI position
      if (paidEmis > 0) {
        breakpoints.push(paidEmis)
      }

      // Find first cancelled EMI position
      const firstCancelledEmi = this.currentBorrowerEmiSchedule.findIndex(
        (emi) => emi.emiStatus === 'Cancelled'
      )

      if (firstCancelledEmi !== -1) {
        const firstCancelledPosition = firstCancelledEmi + 1 // Convert to 1-based position
        // Only add if it's different from last paid position
        if (!breakpoints.includes(firstCancelledPosition)) {
          breakpoints.push(firstCancelledPosition)
        }
      }

      return breakpoints.sort((a, b) => a - b) // Sort in ascending order
    },

    // Get first cancelled EMI position for legend display
    firstCancelledEmiPosition() {
      const firstCancelledEmi = this.currentBorrowerEmiSchedule.findIndex(
        (emi) => emi.emiStatus === 'Cancelled'
      )
      return firstCancelledEmi !== -1 ? firstCancelledEmi + 1 : 0
    },
    todayDate() {
      return new Date().toISOString().substr(0, 10)
    },
    currentBorrowerEmiSchedule() {
      if (!this.borrowerTabs.length) return this.emiSchedule

      const currentBorrower = this.borrowerTabs[this.activeBorrowerTab]
      if (!currentBorrower) return []

      return this.emiSchedule.filter(
        (emi) => emi.customer === currentBorrower.id || emi.customerId === currentBorrower.id
      )
    },

    emiStatusData() {
      const schedule = this.currentBorrowerEmiSchedule
      const totalEmis = schedule.length
      const paidEmis = schedule.filter(
        (emi) => emi.emiStatus === 'Paid' || emi.emiStatus === 'Minimum Amount Paid'
      ).length
      const pendingEmis = schedule.filter(
        (emi) => emi.emiStatus === 'Pending' || emi.emiStatus === 'Due'
      ).length
      const overdueEmis = schedule.filter(
        (emi) => emi.emiStatus === 'Overdue'
      ).length

      return {
        totalEmis,
        paidEmis,
        pendingEmis,
        overdueEmis
      }
    },

    unpaidEmiCount() {
      return this.currentBorrowerEmiSchedule.filter(
        (emi) =>
          emi.emiStatus === 'Pending' ||
          emi.emiStatus === 'Due' ||
          emi.emiStatus === 'Overdue'
      ).length
    },

    currentBorrower() {
      if (this.borrowerTabs.length === 0) return null
      return this.borrowerTabs[this.activeBorrowerTab] || this.borrowerTabs[0] || null
    },

    tenureChangeText() {
      const totalEmis = this.emiStatusData.totalEmis
      const paidEmis = this.emiStatusData.paidEmis
      // Original closure would be at the rightmost enabled position (considering cancelled EMIs)
      const originalClosureEmi = this.rightmostEnabledPosition
      const newClosureEmi = this.customTenure

      // Calculate EMI reduction (how many EMIs are saved)
      const emisSaved = originalClosureEmi - newClosureEmi

      if (emisSaved === 0) return 'Same as Original'
      if (emisSaved > 0) return `${emisSaved} EMIs Reduced`
      return `${Math.abs(emisSaved)} EMIs Extended`
    },

    tenureChangeColor() {
      const totalEmis = this.emiStatusData.totalEmis
      const originalClosureEmi = this.rightmostEnabledPosition
      const newClosureEmi = this.customTenure

      const emisSaved = originalClosureEmi - newClosureEmi

      if (emisSaved === 0) return 'text-gray-600'
      if (emisSaved > 0) return 'text-green-600' // EMIs reduced (good)
      return 'text-red-600' // EMIs extended (bad)
    },

    // Validation computed properties
    maxPaymentAmount() {
      return this.foreclosureData.principalOutstanding + this.foreclosureData.accruedInterest
    },

    paymentAmountRules() {
      return [
        (v) => v >= 0 || 'Payment amount cannot be negative',
        (v) =>
          v <= this.maxPaymentAmount ||
          `Payment amount cannot exceed ₹${this.formatCurrency(
            this.maxPaymentAmount
          )} (Principal + Accrued Interest)`,
        (v) => !isNaN(v) || 'Please enter a valid number'
      ]
    },

    accruedInterestRules() {
      return [
        (v) => v >= 0 || 'Accrued interest cannot be negative',
        (v) => !isNaN(v) || 'Please enter a valid number'
      ]
    },

    amountRules() {
      return [
        (v) => v >= 0 || 'Amount cannot be negative',
        (v) => !isNaN(v) || 'Please enter a valid number'
      ]
    },
    isValidForPayment() {
      const status = this.getLoanStatusString()
      const isClosed = ['Foreclosed', 'Settled', 'Completed', 'Closed'].includes(status)

      // If loan is closed, it's never valid for payment
      if (isClosed) return false

      // Otherwise, check if there are pending EMIs
      const hasPendingEmis =
        (this.emiStatusData?.pendingEmis || 0) + (this.emiStatusData?.overdueEmis || 0) > 0
      return hasPendingEmis
    },
    closedStatusText() {
      const status = this.getLoanStatusString()
      if (status === 'Foreclosed') return 'FORECLOSED'
      if (status === 'Completed') return 'COMPLETED'
      if (status === 'Closed') return 'CLOSED'
      if (status === 'Settled') return 'SETTLED'
      return 'SETTLED'
    },
    closedStatusColorClass() {
      if (this.closedStatusText === 'FORECLOSED') return 'text-red-500'
      if (this.closedStatusText === 'COMPLETED') return 'text-primary'
      return 'text-green-500'
    }
  },

  mounted() {
    // Initialize with first borrower if multiple borrowers exist
    if (this.borrowerTabs.length > 0) {
      this.activeBorrowerTab = 0
    }
    this.initializeTenureSlider()
    this.calculateForeclosure()
    this.setDefaultPartialAmount()
  },

  methods: {
    getLoanStatusString() {
      return (
        this.selectedCase?.loanDetails?.currentStatus ||
        this.selectedCase?.caseStatus ||
        this.loanDetails?.status ||
        this.selectedCase?.status ||
        ''
      )
    },
    onPenaltyChargesChanged(charges) {
      // Validate penalty charges before applying
      const validatedCharges = charges.map((charge) => ({
        ...charge,
        amount: Math.max(0, parseFloat(charge.amount) || 0)
      }))
      this.penaltyCharges = validatedCharges
      this.validateAndCalculate()
    },

    onDiscountWaiversChanged(discounts) {
      // Validate discount waivers before applying
      const maxDiscount = this.getTotalAmountBeforeDiscounts()

      let totalDiscounts = 0

      const validatedDiscounts = discounts.map((discount) => {
        const amount = Math.max(0, parseFloat(discount.amount) || 0)
        totalDiscounts += amount
        return {
          ...discount,
          amount: amount
        }
      })
      console.log(validatedDiscounts)

      // Ensure total discounts don't exceed available amount
      // if (totalDiscounts > maxDiscount) {
      //   this.$toast.warning(
      //     `Total discounts (₹${this.formatCurrency(
      //       totalDiscounts
      //     )}) cannot exceed available amount (₹${this.formatCurrency(maxDiscount)})`
      //   )
      //   return
      // }

      this.discountWaivers = validatedDiscounts
      this.validateAndCalculate()
    },

    getTotalPenaltyCharges() {
      return this.penaltyCharges.reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0)
    },

    getTotalDiscountWaivers() {
      return this.discountWaivers.reduce(
        (sum, discount) => sum + (parseFloat(discount.amount) || 0),
        0
      )
    },

    calculateLateFees() {
      let totalLateFees = 0
      this.currentBorrowerEmiSchedule.forEach((emi) => {
        if (emi.emiStatus === 'Overdue') {
          // Calculate late fees based on overdue days
          const overdueDays = this.calculateOverdueDays(emi.dueDate)
          const lateFeeRate = this.loanDetails.lateFeeRate || 2 // 2% default
          totalLateFees += ((emi.emiAmount * lateFeeRate) / 100) * Math.ceil(overdueDays / 30)
        }
      })
      return totalLateFees
    },

    calculatePrepaymentCharges() {
      // Calculate prepayment charges based on borrower's outstanding principal
      const prepaymentRate = this.loanDetails.prepaymentCharges || 0
      const principalOutstanding = this.foreclosureData.principalOutstanding || 0

      if (prepaymentRate > 0 && principalOutstanding > 0) {
        return (prepaymentRate / 100) * principalOutstanding
      }
      return 0
    },

    calculateOverdueDays(dueDate) {
      const today = new Date()
      const due = new Date(dueDate)
      const diffTime = today - due
      return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)))
    },

    // Handle manual accrued interest changes
    onAccruedInterestChange() {
      this.manualAccruedInterest = true
      this.validateAndCalculate()
    },

    // Validation methods
    validateAndCalculate() {
      if (this.validateInputs()) {
        this.calculateForeclosure()
      }
    },

    validateInputs() {
      let isValid = true
      const errors = []

      // Validate payment amount
      if (this.partialAmount < 0) {
        errors.push('Payment amount cannot be negative')
        isValid = false
      }
      if (this.partialAmount > this.maxPaymentAmount) {
        errors.push(`Payment amount cannot exceed ₹${this.formatCurrency(this.maxPaymentAmount)}`)
        isValid = false
      }

      // Validate accrued interest
      if (this.foreclosureData.accruedInterest < 0) {
        errors.push('Accrued interest cannot be negative')
        isValid = false
      }

      // Show validation errors
      if (errors.length > 0) {
        // errors.forEach((error) => this.$toast.error(error))
      }

      return isValid
    },

    getPaymentAmountErrors() {
      const errors = []
      if (this.partialAmount < 0) {
        errors.push('Payment amount cannot be negative')
      }
      if (this.partialAmount > this.maxPaymentAmount) {
        errors.push(`Cannot exceed ₹${this.formatCurrency(this.maxPaymentAmount)}`)
      }
      return errors
    },

    getAccruedInterestErrors() {
      const errors = []
      if (this.foreclosureData.accruedInterest < 0) {
        errors.push('Accrued interest cannot be negative')
      }
      return errors
    },

    getTotalAmountBeforeDiscounts() {
      return (
        this.foreclosureData.principalOutstanding +
        this.foreclosureData.accruedInterest +
        this.getTotalPenaltyCharges()
      )
    },

    calculateForeclosure() {
      // Calculate principal outstanding from current borrower's EMI schedule
      const schedule = this.currentBorrowerEmiSchedule
      const unPaidEmis = schedule.filter((emi) =>
        ['Pending', 'Due', 'Overdue'].includes(emi.emiStatus)
      )

      // Use loanDetails.principalOutstanding as primary source, fallback to EMI outstanding if available and valid
      let principalOutstanding = parseFloat(this.loanDetails.principalOutstanding) || 0

      // If we have unpaid EMIs and the first one has a valid outstanding amount, consider using it
      // as it might represent the principal balance at that point in time.
      if (
        unPaidEmis.length > 0 &&
        unPaidEmis[0].outstandingAmount &&
        parseFloat(unPaidEmis[0].outstandingAmount) > 0
      ) {
        principalOutstanding = parseFloat(unPaidEmis[0].outstandingAmount)
      }

      this.foreclosureData.principalOutstanding = principalOutstanding

      // Only auto-calculate accrued interest if it hasn't been manually set
      if (!this.manualAccruedInterest) {
        // Calculate accrued interest with proper daily calculation and validation
        const properAccruedInterest = this.calculateAccruedInterest()
        const interestWaiverAmount = this.discountWaivers
          .filter((d) => d.discountType.toLowerCase().includes('interest'))
          .reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0)

        // Apply validation constraints
        const calculatedAccruedInterest = Math.max(0, properAccruedInterest - interestWaiverAmount)

        this.foreclosureData.accruedInterest = Math.round(calculatedAccruedInterest * 100) / 100
      }

      // Calculate total penalty charges and discounts
      const totalPenaltyCharges = this.getTotalPenaltyCharges()
      const totalDiscountWaivers = this.getTotalDiscountWaivers()

      // Store total discounts for reference
      this.foreclosureData.totalDiscounts = Math.round(totalDiscountWaivers * 100) / 100

      // Calculate penalty amount (before discounts)
      this.foreclosureData.penaltyAmount = Math.round(totalPenaltyCharges * 100) / 100

      // Calculate subtotal (principal + accrued interest)
      this.foreclosureData.subtotal =
        (parseFloat(this.foreclosureData.principalOutstanding) || 0) +
        (parseFloat(this.foreclosureData.accruedInterest) || 0)

      // Calculate total foreclosure amount with penalties and discounts applied
      const grossAmount = this.foreclosureData.subtotal + totalPenaltyCharges
      this.foreclosureData.totalForeclosureAmount = Math.max(
        0,
        Math.round((grossAmount - totalDiscountWaivers) * 100) / 100
      )

      // Handle partial foreclosure with validation
      if (this.loanClosureType === 'prepayment' && this.partialAmount > 0 && !this.isFullPayment) {
        // Ensure partial amount doesn't exceed maximum allowed
        const validatedPartialAmount = Math.min(this.partialAmount, this.maxPaymentAmount)

        // For partial payments, the foreclosure amount is the partial amount
        this.foreclosureData.totalForeclosureAmount = Math.round(validatedPartialAmount * 100) / 100

        // Update partial amount if it was capped
        if (validatedPartialAmount !== this.partialAmount) {
          this.partialAmount = validatedPartialAmount
        }

        // Calculate prepayment impact
        this.calculatePrepaymentImpact()
      } else if (this.isFullPayment) {
        // For full payment, include all charges and apply all discounts
        this.partialAmount = Math.round(this.foreclosureData.totalForeclosureAmount * 100) / 100
        this.prepaymentImpact = null
      } else {
        this.prepaymentImpact = null
      }
    },

    calculatePrepaymentImpact() {
      if (!this.partialAmount || this.partialAmount <= 0) {
        this.prepaymentImpact = null
        return
      }

      const currentPrincipal = parseFloat(this.foreclosureData.principalOutstanding) || 0
      const unpaidEmis = this.currentBorrowerEmiSchedule.filter(
        (emi) =>
          emi.emiStatus !== 'Paid' &&
          emi.emiStatus !== 'Minimum Amount Paid' &&
          emi.emiStatus !== 'Late'
      )
      const currentEmi =
        unpaidEmis.length > 0
          ? parseFloat(unpaidEmis[0].emiAmount) || 0
          : parseFloat(
              (this.currentBorrower ? this.currentBorrower.emiAmount : this.loanDetails.emiAmount) ||
                0
            )
      const annualInterestRate = parseFloat(this.loanDetails.interestRate) || 0
      const remainingTenure = this.emiStatusData.pendingEmis + this.emiStatusData.overdueEmis

      const netPrepayment = parseFloat(this.partialAmount) || 0
      const newPrincipal = Math.max(0, currentPrincipal - netPrepayment)

      let impact = {
        newOutstandingPrincipal: newPrincipal,
        interestSavings: 0,
        tenureReduction: 0,
        newEmiAmount: currentEmi
      }

      if (newPrincipal > 0 && annualInterestRate > 0 && remainingTenure > 0 && currentEmi > 0) {
        if (this.prepaymentApplication === 'reduce_tenure') {
          // Use utility function to calculate new tenure
          const newTenure = calculateTenure(newPrincipal, currentEmi, annualInterestRate)

          if (newTenure < remainingTenure && newTenure !== Infinity) {
            impact.tenureReduction = Math.max(0, remainingTenure - newTenure)
            // impact.interestSavings = currentEmi * impact.tenureReduction
          }
        } else if (this.prepaymentApplication === 'reduce_emi') {
          // Use utility function to calculate new EMI
          const newEmi = calculateEMI(newPrincipal, remainingTenure, annualInterestRate)

          if (newEmi > 0 && newEmi < currentEmi) {
            impact.newEmiAmount = newEmi
            // impact.interestSavings = (currentEmi - newEmi) * remainingTenure
          }
        } else if (this.prepaymentApplication === 'custom_tenure') {
          // For custom tenure, use the calculated values from slider
          this.calculateCustomTenureImpact()
          return // Return early as impact is set in calculateCustomTenureImpact
        }
      } else if (newPrincipal <= 0) {
        // If prepayment covers entire principal, tenure becomes 0
        impact.tenureReduction = remainingTenure
        // impact.interestSavings = currentEmi * remainingTenure
        impact.newOutstandingPrincipal = 0
      }

      this.prepaymentImpact = impact
    },

    calculateAccruedInterest() {
      // Calculate daily accrued interest from last paid EMI date to foreclosure date
      const foreclosureDate = new Date(this.closureDetails.closureDate)
      const annualInterestRate = parseFloat(this.loanDetails.interestRate) || 0
      const dailyInterestRate = annualInterestRate / 100 / 365 // Convert annual % to daily decimal

      // Find the last paid EMI to determine the start date for accrued interest calculation
      const paidEmis = this.emiSchedule
        .filter((emi) => emi.emiStatus === 'Paid' || emi.emiStatus === 'Minimum Amount Paid')
        .sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate))

      let startDate
      if (paidEmis.length > 0) {
        // Start from the last paid EMI due date
        startDate = new Date(paidEmis[0].dueDate)
      } else {
        // If no EMIs are paid, start from loan disbursement date or first EMI due date
        const firstEmi = this.emiSchedule[0]
        startDate = firstEmi ? new Date(firstEmi.dueDate) : new Date()
      }

      // Calculate days between start date and foreclosure date
      const timeDiff = foreclosureDate.getTime() - startDate.getTime()
      const daysDiff = Math.max(0, Math.ceil(timeDiff / (1000 * 60 * 60 * 24)))

      // Calculate accrued interest: Principal Outstanding × Daily Interest Rate × Days
      const principalOutstanding = this.foreclosureData.principalOutstanding || 0
      const accruedInterest = principalOutstanding * dailyInterestRate * daysDiff
      return Math.max(0, accruedInterest)
    },

    resetCalculation() {
      this.loanClosureType = 'prepayment'
      this.isFullPayment = false
      this.partialAmount = 0
      this.prepaymentApplication = 'reduce_tenure'
      this.prepaymentImpact = null
      this.manualAccruedInterest = false // Reset manual accrued interest flag

      // Reset tenure slider
      this.initializeTenureSlider()

      this.calculateForeclosure()
      this.setDefaultPartialAmount()
      this.closureDetails = {
        closureReason: '',
        remarks: '',
        closureDate: new Date().toISOString().substr(0, 10)
      }
      this.$toast.info('Calculation reset to default values')
    },
    processForeclosure() {
      // if (!this.closureDetails.closureReason) {
      //   this.$toast.error('Please select a closure reason')
      //   return
      // }

      this.confirmDialog = true
    },

    async confirmForeclosure() {
      // Check if workflow is complete
      if (this.$refs.workflowRef && !this.$refs.workflowRef.isWorkflowComplete()) {
        this.$toast.error('Please complete all mandatory workflow requirements before closure')
        this.processing = false
        return
      }

      this.processing = true

      try {
        // Perform final validation before processing
        if (!this.validateInputs()) {
          this.processing = false
          return
        }

        // Additional business logic validation
        const businessValidation = this.performBusinessValidation()
        if (!businessValidation.isValid) {
          businessValidation.errors.forEach((error) => this.$toast.error(error))
          this.processing = false
          return
        }

        // Calculate tenure reduction for partial payments
        let tenureReduction = 0
        if (this.loanClosureType === 'prepayment' && this.prepaymentImpact) {
          tenureReduction = this.prepaymentImpact.tenureReduction || 0
        }

        // Calculate net outstanding principal after discounts
        const netOutstandingPrincipal = this.isFullPayment
          ? 0 // For full payment, outstanding becomes 0
          : Math.max(0, this.foreclosureData.principalOutstanding - (this.partialAmount || 0))

        const payload = {
          loanClosureType: this.isFullPayment ? 'foreclosure' : this.loanClosureType,
          totalAmtPaid: this.foreclosureData?.totalForeclosureAmount,
          accruedIntrest: this.foreclosureData.accruedInterest || 0,
          discount: this.getTotalDiscountWaivers(),
          penalty: this.getTotalPenaltyCharges(),
          actualPrincipalAmt: netOutstandingPrincipal,
          remarks: this.closureDetails.remarks || '',
          tenureReduction: tenureReduction,
          amountReduction:
            this.prepaymentApplication === 'reduce_tenure' ? 0 : this.partialAmount || 0,
          penaltyCharges: this.penaltyCharges,
          discountWaivers: this.discountWaivers,
          customerId: this.currentBorrower?.id || null,
          borrowerName: this.currentBorrower?.name || null,
          borrowerShare: this.currentBorrower?.share || 100,
          workflow: this.closureWorkflow
        }

        const response = await this.$axios.put(`/collection/loan/${this.caseId}/closure`, payload)

        const borrowerText = this.currentBorrower ? ` for ${this.currentBorrower.name}` : ''

        if (this.isFullPayment) {
          this.$toast.success(`Full foreclosure processed successfully${borrowerText}!`)
        } else {
          this.$toast.success(
            `Partial payment processed successfully${borrowerText}! ${
              tenureReduction > 0
                ? `Tenure reduced by ${tenureReduction} months.`
                : 'EMI amount updated.'
            }`
          )
        }

        this.$emit('foreclosure-processed', {
          ...response.data,
          caseStatus: this.isFullPayment ? 'Foreclosed' : 'Active',
          foreclosureAmount: this.foreclosureData.totalForeclosureAmount,
          foreclosureDate: this.closureDetails.closureDate,
          loanClosureType: this.loanClosureType,
          tenureReduction: tenureReduction,
          amountReduction: payload.amountReduction,
          closureReason: this.closureDetails.closureReason,
          remarks: this.closureDetails.remarks,
          principalOutstanding: this.foreclosureData.principalOutstanding,
          accruedInterest: this.foreclosureData.accruedInterest,
          penaltyAmount: this.getTotalPenaltyCharges(),
          totalDiscounts: this.getTotalDiscountWaivers(),
          penaltyCharges: this.penaltyCharges,
          discountWaivers: this.discountWaivers
        })

        this.confirmDialog = false
      } catch (error) {
        const errorMessage = error?.response?.data?.message || 'Failed to process foreclosure'
        this.$toast.error(errorMessage)
        console.error('Foreclosure processing error:', error)
      } finally {
        this.processing = false
      }
    },

    handleWorkflowSaved({ workflowData }) {
      this.closureWorkflow = workflowData
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('en-IN').format(value || 0)
    },

    formatDate(dateString) {
      return this.$dayjs(dateString).format('DD MMM YYYY')
    },

    getStatusColor(status) {
      return getEmiStatusColor(status)
    },

    calculatePrincipalPaid() {
      // Calculate total principal paid from current borrower's EMIs with 'Paid' or 'Minimum Amount Paid' status
      return this.currentBorrowerEmiSchedule
        .filter(
          (emi) =>
            emi.emiStatus === 'Paid' ||
            emi.emiStatus === 'Minimum Amount Paid' ||
            emi.emiStatus === 'Late'
        )
        .reduce((total, emi) => total + (emi.principalAmount || 0), 0)
    },

    calculateInterestPaid() {
      // Calculate total interest paid from current borrower's EMIs with 'Paid' or 'Minimum Amount Paid' status
      return this.currentBorrowerEmiSchedule
        .filter(
          (emi) =>
            emi.emiStatus === 'Paid' ||
            emi.emiStatus === 'Minimum Amount Paid' ||
            emi.emiStatus === 'Late'
        )
        .reduce((total, emi) => total + (emi.interestAmount || 0), 0)
    },
    getTotalAmountDue() {
      const total = this.foreclosureData.principalOutstanding + this.foreclosureData.accruedInterest
      return Math.round(total * 100) / 100
    },

    performBusinessValidation() {
      const errors = []
      let isValid = true

      // Check if payment amount is reasonable
      if (this.partialAmount > 0 && this.partialAmount < 100) {
        errors.push('Payment amount seems too low. Please verify the amount.')
        isValid = false
      }

      // Check if total discounts exceed reasonable limits
      const totalDiscounts = this.getTotalDiscountWaivers()
      const totalAmount = this.getTotalAmountBeforeDiscounts()
      if (totalDiscounts > totalAmount * 0.9) {
        // 90% discount limit
        errors.push('Total discounts cannot exceed 90% of the total amount')
        isValid = false
      }

      // Check if penalties are reasonable
      const totalPenalties = this.getTotalPenaltyCharges()
      if (totalPenalties > this.foreclosureData.principalOutstanding) {
        errors.push('Total penalties cannot exceed the principal outstanding amount')
        isValid = false
      }

      // Ensure final amount is positive
      const finalAmount = totalAmount - totalDiscounts
      if (finalAmount <= 0) {
        errors.push('Final payment amount must be greater than zero')
        isValid = false
      }

      return { isValid, errors }
    },

    setDefaultPartialAmount() {
      if (this.isFullPayment) {
        // For full payment, calculate the total amount including penalties and discounts
        this.calculateForeclosure() // Calculate first to get updated foreclosure amount
        this.partialAmount = Math.round(this.foreclosureData.totalForeclosureAmount * 100) / 100
      } else {
        this.partialAmount = ''
        this.calculateForeclosure()
      }
    },

    getPrePaymentPenalty(prePayment) {
      if (!prePayment || !prePayment.penalty) return []
      return [{ chargeType: 'Penalty Applied', amount: prePayment.penalty }]
    },

    getPrePaymentDiscount(prePayment) {
      if (!prePayment || !prePayment.discount) return []
      return [{ discountType: 'Discount Authorized', amount: prePayment.discount }]
    },

    async downloadPrePaymentSummary(prePayment) {
      this.selectedPrePaymentPdfData = prePayment
      this.pdfGenerating = true
      try {
        // Wait for next tick to ensure any updates in the template finish rendering
        await this.$nextTick()

        const element = this.$refs.prePaymentPdfComponent?.getHtmlElement()
        if (!element) {
          throw new Error('PDF Template not found')
        }

        const opt = {
          margin: [10, 5, 10, 5],
          filename: `Prepayment_Summary_${this.loanDetails.caseNo || 'Loan'}_${this.formatDate(
            prePayment.date
          ).replace(/\//g, '-')}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, windowWidth: 800, width: 800 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'legacy'] }
        }

        await html2pdf().set(opt).from(element).save()
      } catch (error) {
        console.error('Error generating PDF:', error)
        this.$toast.error('Failed to generate PDF summary')
      } finally {
        this.pdfGenerating = false
        this.selectedPrePaymentPdfData = null
      }
    },

    getFinalAmount() {
      if (this.isFullPayment) {
        return this.foreclosureData.totalForeclosureAmount
      }
      return (
        this.getTotalAmountDue() -
        (this.prepaymentImpact?.prepaymentAmount || 0) +
        this.getTotalPenaltyCharges() -
        this.foreclosureData.totalDiscounts
      )
    },

    getCustomerAddress() {
      const addrList = this.currentBorrower?.customerData?.customerAddress
      if (!addrList || addrList.length === 0) return 'Not available'
      const addr = addrList[0]
      const parts = [
        addr.addressLine1,
        addr.addressLine2,
        addr.city,
        addr.state,
        addr.pinCode
      ].filter(Boolean)
      return parts.join(', ')
    },

    formatCurrencyPdf(value) {
      const val = Number(value) || 0
      return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(val)
    },

    handleFullPaymentToggle() {
      if (this.isFullPayment) {
        this.prepaymentApplication = ''
      } else {
        // Reset to default when unchecking full payment
        this.prepaymentApplication = 'reduce_tenure'
        this.initializeTenureSlider()
      }
      this.setDefaultPartialAmount()
    },

    getBorrowerLoanAmount() {
      // Return the original loan amount for this borrower based on their EMI schedule
      // This is used for display purposes in the summary
      const schedule = this.currentBorrowerEmiSchedule
      if (schedule.length === 0) return this.loanDetails.loanAmountSanctioned || 0

      // Calculate total principal from all EMIs (paid + unpaid)
      const totalPrincipal = schedule.reduce((sum, emi) => sum + (emi.principalAmount || 0), 0)
      return totalPrincipal || this.loanDetails.loanAmountSanctioned || 0
    },

    initializeTenureSlider() {
      // Calculate current EMI amount
      const unpaidEmis = this.currentBorrowerEmiSchedule.filter(
        (emi) =>
          emi.emiStatus !== 'Paid' &&
          emi.emiStatus !== 'Minimum Amount Paid' &&
          emi.emiStatus !== 'Late'
      )

      this.currentEmiAmount =
        unpaidEmis.length > 0
          ? unpaidEmis[0].emiAmount
          : (this.currentBorrower ? this.currentBorrower.emiAmount : this.loanDetails.emiAmount) ||
            0

      // Calculate EMI counts for slider logic
      const totalEmis = this.emiStatusData.totalEmis
      const paidEmis = this.emiStatusData.paidEmis
      const pendingEmis = this.emiStatusData.pendingEmis + this.emiStatusData.overdueEmis

      // Calculate remaining tenure (pending + overdue)
      this.originalRemainingTenure = pendingEmis

      // Set slider bounds: from minimum enabled position to total EMIs
      this.minTenure = Math.max(1, paidEmis + 1) // Can't close before all paid EMIs + 1
      this.maxTenure = totalEmis

      // Set initial custom tenure to the rightmost enabled position
      // This accounts for cancelled EMIs at the end
      this.customTenure = this.rightmostEnabledPosition
      this.customEmiAmount = this.currentEmiAmount

      // Ensure custom tenure is within allowed bounds
      this.customTenure = Math.min(Math.max(this.customTenure, this.minTenure), this.maxTenure)
    },

    onTenureSliderChange() {
      if (this.customTenure <= 0 || !this.foreclosureData.principalOutstanding) {
        this.customEmiAmount = 0
        return
      }

      const annualInterestRate = this.loanDetails.interestRate || 0
      const prepaymentAmount = this.partialAmount || 0
      const principalOutstanding = this.foreclosureData.principalOutstanding
      const paidEmis = this.emiStatusData.paidEmis

      // Calculate new principal after prepayment
      const newPrincipal = Math.max(0, principalOutstanding - prepaymentAmount)

      if (newPrincipal <= 0) {
        this.customEmiAmount = 0
        this.calculateCustomTenureImpact()
        return
      }

      // Calculate remaining EMIs after the closure position
      // If closing at EMI 30 out of 60, then 30 EMIs will be paid (including paid ones)
      const totalEmisToComplete = this.customTenure
      const remainingEmisToComplete = Math.max(0, totalEmisToComplete - paidEmis)

      if (remainingEmisToComplete === 0) {
        this.customEmiAmount = 0
        this.calculateCustomTenureImpact()
        return
      }

      // Calculate new EMI based on reduced principal and remaining EMIs to complete
      this.customEmiAmount =
        Math.round(calculateEMI(newPrincipal, remainingEmisToComplete, annualInterestRate) * 100) /
        100

      // Update prepayment impact for custom tenure
      this.calculateCustomTenureImpact()
    },

    calculateCustomTenureImpact() {
      if (this.prepaymentApplication !== 'custom_tenure') return

      const currentPrincipal = this.foreclosureData.principalOutstanding || 0
      const prepaymentAmount = this.partialAmount || 0
      const totalEmis = this.emiStatusData.totalEmis
      const paidEmis = this.emiStatusData.paidEmis
      const rightmostEnabledPosition = this.rightmostEnabledPosition

      // Calculate new principal after prepayment
      const newPrincipal = Math.max(0, currentPrincipal - prepaymentAmount)

      if (newPrincipal <= 0) {
        // If prepayment covers entire principal
        const originalRemainingEmis = rightmostEnabledPosition - paidEmis
        this.prepaymentImpact = {
          newOutstandingPrincipal: 0,
          interestSavings: this.currentEmiAmount * originalRemainingEmis,
          tenureReduction: originalRemainingEmis,
          newEmiAmount: 0,
          customTenure: this.customTenure,
          prepaymentAmount: prepaymentAmount
        }
        return
      }

      // Calculate EMIs to be completed after closure position
      const totalEmisToComplete = this.customTenure
      const remainingEmisToComplete = Math.max(0, totalEmisToComplete - paidEmis)

      if (remainingEmisToComplete === 0) {
        // Immediate closure scenario
        const originalRemainingEmis = rightmostEnabledPosition - paidEmis
        this.customEmiAmount = 0
        this.prepaymentImpact = {
          newOutstandingPrincipal: 0,
          interestSavings: this.currentEmiAmount * originalRemainingEmis,
          tenureReduction: originalRemainingEmis,
          newEmiAmount: 0,
          customTenure: this.customTenure,
          prepaymentAmount: prepaymentAmount
        }
        return
      }

      // Calculate what the EMI would be with the new principal and remaining EMIs to complete
      const annualInterestRate = this.loanDetails.interestRate || 0
      const newEmiForCustomTenure = calculateEMI(
        newPrincipal,
        remainingEmisToComplete,
        annualInterestRate
      )

      // Update the custom EMI amount display
      this.customEmiAmount = Math.round(newEmiForCustomTenure * 100) / 100

      // Calculate total payment amounts for comparison
      const originalRemainingEmis = rightmostEnabledPosition - paidEmis
      const originalTotalPayment = this.currentEmiAmount * originalRemainingEmis
      const newTotalPayment = this.customEmiAmount * remainingEmisToComplete + prepaymentAmount

      // Calculate interest savings (including the benefit of prepayment)
      const interestSavings = Math.max(0, originalTotalPayment - newTotalPayment)

      // Calculate actual tenure reduction (how many EMIs are saved)
      const tenureReduction = originalRemainingEmis - remainingEmisToComplete

      this.prepaymentImpact = {
        newOutstandingPrincipal: Math.round(newPrincipal * 100) / 100,
        interestSavings: Math.round(interestSavings * 100) / 100,
        tenureReduction: Math.max(0, tenureReduction),
        newEmiAmount: Math.round(this.customEmiAmount * 100) / 100,
        customTenure: this.customTenure,
        prepaymentAmount: prepaymentAmount
      }
    }
  }
}
</script>

<style scoped>
.summary-item,
.status-item {
  padding: 6px;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
  transition: all 0.2s ease;
}

.summary-item:hover,
.status-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.summary-item:nth-child(2) {
  border-left-color: #f59e0b;
}

.summary-item:nth-child(3) {
  border-left-color: #ef4444;
}

.summary-item:nth-child(4) {
  border-left-color: #10b981;
}

.status-item:nth-child(2) {
  border-left-color: #10b981;
}

.status-item:nth-child(3) {
  border-left-color: #ef4444;
}

.status-item:nth-child(4) {
  border-left-color: #f59e0b;
}

.loan-foreclosure-view {
  min-height: 75vh;
}

/* Dark mode support */
.dark-mode .loan-foreclosure-view {
  background-color: #1e1e1e;
  color: #e0e0e0;
}

.dark-mode .summary-item,
.dark-mode .status-item {
  background: #2d2d2d;
  color: #e0e0e0;
}

/* Responsive design */
@media (max-width: 768px) {
  .grid {
    grid-template-columns: 1fr;
  }

  .loan-foreclosure-view {
    padding: 10px;
  }
}

/* Custom table styling */
table {
  border-collapse: collapse;
}

table th,
table td {
  border: 1px solid #e5e7eb;
}

table th {
  background-color: #f9fafb;
  font-weight: 600;
}

table tr:hover {
  background-color: #f3f4f6;
}

/* Payment summary styling */
.payment-summary {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #0ea5e9;
}

/* Form section styling */
.form-section {
  background: #fafafa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

/* Improved spacing */
.space-y-3 > * + * {
  margin-top: 0.75rem;
}

.loan-status-watermark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-30deg);
  font-size: 8rem;
  font-weight: 900;
  opacity: 0.1;
  pointer-events: none;
  z-index: 1;
  white-space: nowrap;
  letter-spacing: 0.8rem;
  text-transform: uppercase;
}

.relative {
  position: relative;
}

.overflow-hidden {
  overflow: hidden;
}
</style>
